<template>
    <rkm-form ref="Form">
        <rkm-row>
            <rkm-col cols="3">
                <rkm-form-input
                    disabled
                    label="Número de recebimento"
                    v-model="data.model.numeroRecebimento"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="3">
                <rkm-form-date-picker
                    :disabled="!data.additionals.permiteEdicao"
                    :validations="validations.dataSolicitacao"
                    label="Data da solicitação"
                    v-model="data.model.solicitadoEm"
                />
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-select
                    :disabled="!data.additionals.permiteEdicao"
                    :options="data.form.tipos"
                    :validations="validations.tipo"
                    label="Tipo"
                    text-field="nome"
                    v-model="data.model.tipoId"
                    value-field="id"
                    show-default-option
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-select
                    :disabled="!data.additionals.permiteEdicao"
                    :options="data.form.unidades"
                    :validations="validations.unidadeSolicitante"
                    label="Unidade solicitante"
                    text-field="nomeFantasia"
                    v-model="data.model.unidadeSolicitanteId"
                    value-field="id"
                    show-default-option
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="!data.additionals.permiteEdicao"
                    :repository="repositories.usuario"
                    :request-query-string-params="queryStringParams.usuario"
                    :search-fields="fields.usuario"
                    :validations="validations.usuarioSolicitante"
                    label="Médico solicitante"
                    text-field="cidadaoNome"
                    uppercase
                    v-model="data.model.usuarioSolicitante"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-button
                            :disabled="!data.additionals.permiteEdicao"
                            @click.stop="$refs.ModalUsuarioSimplificado.open()"
                            color="primary"
                            title="Cadastrar novo usuário"
                        >
                            <rkm-icon icon="plus" />
                        </rkm-button>
                    </rkm-input-group-append>
                </rkm-form-input-autocomplete>
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="!data.additionals.permiteEdicao"
                    :repository="repositories.cid"
                    :search-fields="fields.cid"
                    :validations="validations.cid"
                    label="CID"
                    :text-field="cid => `${formatCid(cid.codigo)} - ${cid.nome}`"
                    uppercase
                    v-model="data.model.cid"
                >
                    <template v-slot:itemLabel="{ item }">
                        {{ item.codigo | formatCid }} - {{ item.nome }}
                    </template>
                </rkm-form-input-autocomplete>
            </rkm-col>
        </rkm-row>

        <rkm-area title="Devolução do processo">
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="!data.additionals.permiteEdicao"
                        :max-date="data.additionals.dataAtual"
                        :validations="validations.processoDevolvidoEm"
                        label="Data"
                        v-model="data.model.processoDevolvidoEm"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :disabled="!data.additionals.permiteEdicao || !data.model.processoDevolvidoEm"
                        :validations="validations.motivoProcessoDevolvido"
                        label="Motivo"
                        v-model="data.model.motivoProcessoDevolvido"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Devolução dos medicamentos">
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="!data.additionals.permiteEdicao"
                        :max-date="data.additionals.dataAtual"
                        :validations="validations.medicamentoDevolvidoEm"
                        label="Data"
                        v-model="data.model.medicamentoDevolvidoEm"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :disabled="!data.additionals.permiteEdicao || !data.model.medicamentoDevolvidoEm"
                        :validations="validations.motivoMedicamentoDevolvido"
                        label="Motivo"
                        v-model="data.model.motivoMedicamentoDevolvido"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Observações">
            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :disabled="!data.additionals.permiteEdicao"
                        :validations="validations.observacoes"
                        v-model="data.model.observacoes"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Observações DRS">
            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :disabled="!data.additionals.permiteEdicao"
                        :validations="validations.observacoesDrs"
                        v-model="data.model.observacoesDrs"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalUsuarioSimplificado
            ref="ModalUsuarioSimplificado"
            @save="setUsuarioSolicitante"
        />
    </rkm-form>
</template>

<script>
// Components.
import ModalUsuarioSimplificado from '@/components/cadastro/usuario/components/cadastro-simplificado/Modal'

// Others.
import formatsFilter from '@/filters/formats'
import { repository, formats } from '@/mixins'
import processoItemMesStatus from '@/enums/farmacia/alto-custo/processoItemMesStatus'

export default {
    components: { ModalUsuarioSimplificado },
    computed: {
        queryStringParams() {

            return {
                usuario: { with_not_regularized: true },
            }
        },
        fields() {

            return {
                usuario:  [
                    { key: 'cadastroCidadao.nome', searchable: true },
                    { key: 'cadastroUsuario.registroNumero', searchable: true },
                ],
                cid:  [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
            }
        },
        validations() {

            return {
                dataSolicitacao: { required: true },
                unidadeSolicitante: { required: true },
                usuarioSolicitante: { required: true },
                tipo: { required: true },
                cid: {},
                processoDevolvidoEm: {},
                motivoProcessoDevolvido: { max: 5000 },
                medicamentoDevolvidoEm: {},
                motivoMedicamentoDevolvido: { max: 5000 },
                observacoes: { max: 5000 },
                observacoesDrs: { max: 5000 },
            }
        }
    },
    created() {

        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        enums: {
            processoItemMesStatus,
        },
    }),
    filters: {
        ...formatsFilter,
    },
    methods: {
        validate() {

            return this.$refs.Form.validate()
        },
        setUsuarioSolicitante(usuario) {

            this.data.model.usuarioSolicitante = {
                id: usuario.id,
                cidadaoNome: usuario.cidadao.nome
            }
        }
    },
    mixins: [repository, formats],
    props: {
        data: { type: Object, required: true },
    },
    watch: {
        'data.model.processoDevolvidoEm': function (data) {

            if (!data) {

                this.data.model.motivoProcessoDevolvido = null
            }

            const get = id => this.data.form.status.find(s => s.id == id)

            this.data.model.itens.map(i => i.meses.map(mes => {

                if (this.data.model.processoDevolvidoEm) {

                    mes.status = get(this.enums.processoItemMesStatus.PROCESSO_DEVOLVIDO)
                }
                else if (!mes.enviadoDrsEm) {

                    mes.status = get(this.enums.processoItemMesStatus.PENDENTE_ENVIO_DRS)
                }
                else if (mes.enviadoDrsEm && !mes.retiradoDrsEm) {

                    mes.status = get(this.enums.processoItemMesStatus.PENTENDE_RETIRADA_DRS)
                }
                else if (mes.enviadoDrsEm && mes.retiradoDrsEm && !mes.retiradoPacienteEm) {

                    mes.status = get(this.enums.processoItemMesStatus.PENDENTE_RETIRADA_PACIENTE)
                }
                else if (mes.enviadoDrsEm && mes.retiradoDrsEm && mes.retiradoPacienteEm) {

                    mes.status = get(this.enums.processoItemMesStatus.RETIRADO_PACIENTE)
                }
            }))
        },
        'data.model.medicamentoDevolvidoEm': function (data) {

            if (!data) {

                this.data.model.motivoMedicamentoDevolvido = null
            }
        },
    }
}
</script>